import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { reportService } from '../services/api';
import { Report } from '../types';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, Button, Typography, Chip, FormControl, InputLabel,
  Select, MenuItem, Stack, IconButton, Tooltip, Container, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, Checkbox,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteIcon from '@mui/icons-material/Delete';

const maskToken = (token: string) =>
  token.length > 4 ? '●'.repeat(token.length - 4) + token.slice(-4) : '●'.repeat(token.length);

const maskOwner = (owner: string) =>
  owner.length > 3 ? owner.slice(0, owner.length - 3) + '●●●' : '●●●';

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

const getCategoryColor = (category: string): ChipColor => {
  const colors: Record<string, ChipColor> = {
    Engineering: 'primary', Product: 'secondary', Design: 'info',
    Marketing: 'success', Sales: 'warning', HR: 'error', Finance: 'info', Other: 'default',
  };
  return colors[category] ?? 'default';
};

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState<Report[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<Report | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Report | null>(null);
  const [revealedTokens, setRevealedTokens] = useState<Set<number>>(new Set());
  const [revealedOwners, setRevealedOwners] = useState<Set<number>>(new Set());
  const [dialogTokenRevealed, setDialogTokenRevealed] = useState(false);
  const [dialogOwnerRevealed, setDialogOwnerRevealed] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const toggleToken = (id: number) =>
    setRevealedTokens(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const toggleOwner = (id: number) =>
    setRevealedOwners(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const toggleSelect = (id: number) =>
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const toggleSelectAll = () =>
    setSelected(selected.size === reports.length ? new Set() : new Set(reports.map(r => r.id)));

  const selectedReports = reports.filter(r => selected.has(r.id));

  const exportColumns = (r: Report) => [
    r.category, r.owner || '', r.token || '', r.status || 'Active',
    r.creator_name || '', new Date(r.created_at).toLocaleDateString(),
    r.date_modified ? new Date(r.date_modified).toLocaleDateString() : '',
    r.old_token || '', r.content || '',
  ];

  const headers = ['Category', 'Owner', 'Token', 'Status', 'Created By', 'Created', 'Modified', 'Old Token', 'Content'];

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.text('Vaults Portal — Export', 14, 14);
    autoTable(doc, { head: [headers], body: selectedReports.map(exportColumns), startY: 20 });
    doc.save('vaults-export.pdf');
  };

  const exportExcel = () => {
    const ws = XLSX.utils.aoa_to_sheet([headers, ...selectedReports.map(exportColumns)]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Entries');
    XLSX.writeFile(wb, 'vaults-export.xlsx');
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = { limit: rowsPerPage, offset: page * rowsPerPage, search: searchTerm, category: categoryFilter };
      const [response, allResponse] = await Promise.all([
        reportService.getAll(params),
        reportService.getAll(),
      ]);
      setReports(response.data);
      setTotal(allResponse.data.length);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); setSelected(new Set()); }, [page, rowsPerPage, searchTerm, categoryFilter]);
  useEffect(() => { reportService.getCategories().then(res => setCategories(res.data)).catch(() => {}); }, []);

  const handleArchiveConfirm = async () => {
    if (!archiveTarget) return;
    const newStatus = archiveTarget.status === 'Inactive' ? 'Active' : 'Inactive';
    try {
      await reportService.update(archiveTarget.id, { status: newStatus });
      setArchiveTarget(null);
      fetchReports();
    } catch (err) {
      console.error('Archive error:', err);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await reportService.delete(deleteTarget.id);
      setDeleteTarget(null);
      fetchReports();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        <Box mb={4}>
          <Typography variant="h4" gutterBottom>Dashboard</Typography>
          <Typography variant="body1" color="text.secondary">Welcome, {user?.name}</Typography>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField placeholder="Search entries..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
              sx={{ flex: 1 }} size="small" />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Category</InputLabel>
              <Select value={categoryFilter} label="Category"
                onChange={(e: SelectChangeEvent) => setCategoryFilter(e.target.value)}>
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((cat) => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
              </Select>
            </FormControl>
            {(searchTerm || categoryFilter) && (
              <Button onClick={() => { setSearchTerm(''); setCategoryFilter(''); }} variant="text">Clear</Button>
            )}
            {selected.size > 0 && (
              <>
                <Button variant="outlined" size="small" onClick={exportPDF}>
                  PDF ({selected.size})
                </Button>
                <Button variant="outlined" size="small" color="success" onClick={exportExcel}>
                  Excel ({selected.size})
                </Button>
              </>
            )}
          </Stack>
        </Paper>

        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.size > 0 && selected.size < reports.length}
                    checked={reports.length > 0 && selected.size === reports.length}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Token</TableCell>
                <TableCell>Status</TableCell>
                {user?.role_id === 1 && <TableCell>Created By</TableCell>}
                <TableCell>Created</TableCell>
                <TableCell>Modified</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={user?.role_id === 1 ? 9 : 8} align="center" sx={{ py: 3 }}>Loading...</TableCell></TableRow>
              ) : reports.length === 0 ? (
                <TableRow><TableCell colSpan={user?.role_id === 1 ? 9 : 8} align="center" sx={{ py: 3 }}>No entries found</TableCell></TableRow>
              ) : reports.map((report) => (
                <TableRow key={report.id} hover selected={selected.has(report.id)}
                  sx={report.status === 'Inactive' ? { opacity: 0.55 } : {}}>
                  <TableCell padding="checkbox">
                    <Checkbox checked={selected.has(report.id)} onChange={() => toggleSelect(report.id)} />
                  </TableCell>
                  <TableCell>
                    <Chip label={report.category} size="small" color={getCategoryColor(report.category)}
                      onClick={() => setCategoryFilter(report.category)} clickable />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {report.owner ? (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <span>{revealedOwners.has(report.id) ? report.owner : maskOwner(report.owner)}</span>
                        <Tooltip title={revealedOwners.has(report.id) ? 'Hide' : 'Show'}>
                          <IconButton size="small" onClick={() => toggleOwner(report.id)}>
                            {revealedOwners.has(report.id) ? <VisibilityOffIcon fontSize="inherit" /> : <VisibilityIcon fontSize="inherit" />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : '-'}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {report.token ? (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <span>{revealedTokens.has(report.id) ? report.token : maskToken(report.token)}</span>
                        <Tooltip title={revealedTokens.has(report.id) ? 'Hide' : 'Reveal'}>
                          <IconButton size="small" onClick={() => toggleToken(report.id)}>
                            {revealedTokens.has(report.id) ? <VisibilityOffIcon fontSize="inherit" /> : <VisibilityIcon fontSize="inherit" />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip label={report.status || 'Active'} size="small"
                      color={report.status === 'Active' ? 'success' : report.status === 'Changed' ? 'warning' : 'default'} />
                  </TableCell>
                  {user?.role_id === 1 && <TableCell>{report.creator_name || '-'}</TableCell>}
                  <TableCell>{new Date(report.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>{report.date_modified ? new Date(report.date_modified).toLocaleDateString() : '-'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => { setSelectedReport(report); setViewOpen(true); }}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => navigate(`/entries/${report.id}`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={report.status === 'Inactive' ? 'Restore' : 'Archive'}>
                      <IconButton size="small" onClick={() => setArchiveTarget(report)}
                        color={report.status === 'Inactive' ? 'default' : 'default'}>
                        {report.status === 'Inactive'
                          ? <UnarchiveIcon fontSize="small" />
                          : <ArchiveIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setDeleteTarget(report)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={total}
            rowsPerPage={rowsPerPage} page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </TableContainer>

        {/* View dialog */}
        <Dialog open={viewOpen} onClose={() => { setViewOpen(false); setSelectedReport(null); setDialogTokenRevealed(false); setDialogOwnerRevealed(false); }} maxWidth="md" fullWidth>
          <DialogTitle>Entry Details</DialogTitle>
          <Divider />
          <DialogContent>
            {selectedReport && (
              <Box mt={1}>
                {[
                  ['Category', selectedReport.category],
                  ['Status', selectedReport.status || 'Active'],
                  ['Creator', selectedReport.creator_name || '-'],
                  ['Date Created', new Date(selectedReport.created_at).toLocaleString()],
                  ['Last Modified', selectedReport.date_modified ? new Date(selectedReport.date_modified).toLocaleString() : '-'],
                  ['Old Token', selectedReport.old_token || '-'],
                ].map(([label, val]) => (
                  <Typography key={label} variant="body2" sx={{ mb: 1 }}>
                    <strong>{label}:</strong> {val}
                  </Typography>
                ))}
                <Typography variant="body2" sx={{ mb: 1 }} component="div">
                  <strong>Owner:</strong>{' '}
                  <Stack direction="row" alignItems="center" spacing={0.5} display="inline-flex">
                    <Box component="span" sx={{ fontFamily: 'monospace' }}>
                      {selectedReport.owner
                        ? (dialogOwnerRevealed ? selectedReport.owner : maskOwner(selectedReport.owner))
                        : '-'}
                    </Box>
                    {selectedReport.owner && (
                      <Tooltip title={dialogOwnerRevealed ? 'Hide' : 'Show'}>
                        <IconButton size="small" onClick={() => setDialogOwnerRevealed(v => !v)}>
                          {dialogOwnerRevealed ? <VisibilityOffIcon fontSize="inherit" /> : <VisibilityIcon fontSize="inherit" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }} component="div">
                  <strong>Token:</strong>{' '}
                  <Stack direction="row" alignItems="center" spacing={0.5} display="inline-flex">
                    <Box component="span" sx={{ fontFamily: 'monospace' }}>
                      {selectedReport.token
                        ? (dialogTokenRevealed ? selectedReport.token : maskToken(selectedReport.token))
                        : '-'}
                    </Box>
                    {selectedReport.token && (
                      <Tooltip title={dialogTokenRevealed ? 'Hide' : 'Reveal'}>
                        <IconButton size="small" onClick={() => setDialogTokenRevealed(v => !v)}>
                          {dialogTokenRevealed ? <VisibilityOffIcon fontSize="inherit" /> : <VisibilityIcon fontSize="inherit" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2"><strong>Content:</strong></Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {selectedReport.content || '-'}
                </Typography>
                <Box mt={1.5} display="flex" justifyContent="flex-end">
                  <Button size="small" variant="outlined"
                    onClick={() => { setViewOpen(false); navigate(`/entries/${selectedReport.id}`); }}>
                    Open Full Entry
                  </Button>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setViewOpen(false); setSelectedReport(null); }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Archive / Restore confirm dialog */}
        <Dialog open={!!archiveTarget} onClose={() => setArchiveTarget(null)} maxWidth="sm">
          <DialogTitle>{archiveTarget?.status === 'Inactive' ? 'Restore Entry' : 'Archive Entry'}</DialogTitle>
          <Divider />
          <DialogContent>
            <Typography>
              {archiveTarget?.status === 'Inactive'
                ? 'Restore this entry to Active status?'
                : 'Archive this entry? It will be set to Inactive.'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setArchiveTarget(null)}>Cancel</Button>
            <Button onClick={handleArchiveConfirm} variant="contained"
              color={archiveTarget?.status === 'Inactive' ? 'primary' : 'warning'}>
              {archiveTarget?.status === 'Inactive' ? 'Restore' : 'Archive'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirm dialog */}
        <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="sm">
          <DialogTitle>Delete Entry</DialogTitle>
          <Divider />
          <DialogContent>
            <Typography>Are you sure you want to permanently delete this entry? This cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} variant="contained" color="error">Delete</Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Container>
  );
};

export default DashboardPage;
